/**
 * @file tier-discount-search.helper.ts
 * @description Helper to initialize tier discount widget on search page
 * Renders into pre-existing container div (similar to dynamicBundle pattern)
 *
 * Container div must exist in search page template:
 * <div id="boost-sd__tier-discount-search-page" class="boost-tier-discount-bundle-search-page"></div>
 */

import { TierDiscountModule } from './tier-discount.module';

const TIER_DISCOUNT_SEARCH_PAGE_CONTAINER_ID = 'boost-sd__tier-discount-search-page';

/**
 * Initializes tier discount widget on search page
 * Uses static method from TierDiscountModule to access DI container
 */
export async function injectTierDiscountForSearchPage(): Promise<void> {
  // Find pre-existing container div
  const tierDiscountContainer = document?.getElementById(TIER_DISCOUNT_SEARCH_PAGE_CONTAINER_ID);

  if (!tierDiscountContainer) {
    console.log(`[TierDiscount] Container not found`);
    return;
  }

  // Check if already initialized
  const isInitialized = tierDiscountContainer.hasAttribute('data-tier-discount-initialized');
  if (isInitialized) {
    return;
  }

  // Mark as initialized to prevent duplicate initialization
  tierDiscountContainer.setAttribute('data-tier-discount-initialized', 'true');

  // Get first product ID from search results for bundle data
  const firstProductElement = document.querySelector('[data-product-id]');
  const productId = firstProductElement?.getAttribute('data-product-id');

  if (!productId) {
    tierDiscountContainer.removeAttribute('data-tier-discount-initialized');
    return;
  }

  // Set product_id in global config for API to use
  if (!window.boostWidgetIntegration) {
    (window as any).boostWidgetIntegration = {};
  }
  // Initialize widget through TierDiscountModule static method
  try {
    await TierDiscountModule.initializeForSearchPage(tierDiscountContainer);

    // Guard against race condition: if resetTierDiscountContainer() was called while we
    // were awaiting the async init (e.g. a non-tiered bundle won a concurrent filter
    // cycle), the 'data-tier-discount-initialized' attribute was removed. Undo the
    // stale render so the winning bundle shows correctly.
    if (!tierDiscountContainer.hasAttribute('data-tier-discount-initialized')) {
      tierDiscountContainer.innerHTML = '';
    }
  } catch (error) {
    tierDiscountContainer.removeAttribute('data-tier-discount-initialized');
  }
}

/**
 * Resets initialization guards and re-injects the tier discount widget.
 * Called after each filter/sort/pagination update when tiered bundle is the latest,
 * ensuring the widget always reflects current filter results.
 */
export function reinitializeTierDiscountForSearchPage(): void {
  const container = document?.getElementById(TIER_DISCOUNT_SEARCH_PAGE_CONTAINER_ID);
  if (!container) return;

  container.removeAttribute('data-tier-discount-initialized');
  container.removeAttribute('data-tier-connected');
  container.removeAttribute('data-tier-events-bound');
  void injectTierDiscountForSearchPage();
}

// Driven by renderRecommendationForSearchPage (dynamicBundle/index.js) which dispatches this
// event after each filter API response — guarantees filter block data (dynamicBundles) is
// populated before initialization, and re-evaluates after every filter/sort/pagination change.
window.addEventListener('boost-sd-tier-discount-search-reinitialize', () => {
  reinitializeTierDiscountForSearchPage();
});
